'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function OverweightCalculator() {
  const t = useTranslations("tools.overweightCalculator");
  const [screenSize, setScreenSize] = useState('mobile');
  const [currentUnit, setCurrentUnit] = useState<'imperial' | 'metric'>('imperial');
  const [showResults, setShowResults] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const [inputs, setInputs] = useState({
    age: 25,
    gender: 'male' as 'male' | 'female',
    feet: 5,
    inches: 10,
    pounds: 160,
    cm: 178,
    kg: 73,
  });

  const [results, setResults] = useState({
    bmi: 0,
    category: '',
    categoryClass: '',
    currentWeight: '',
    healthyRange: '',
    recommendation: '',
  });

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
  };

  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage('');

    try {
      let heightInMeters: number;
      let weightInKg: number;

      if (currentUnit === 'imperial') {
        const { feet, inches, pounds } = inputs;

        if (feet === 0 && inches === 0) {
          throw new Error(t('errorInvalidHeight'));
        }
        if (!pounds || pounds <= 0) {
          throw new Error(t('errorInvalidWeight'));
        }

        const totalInches = (feet * 12) + inches;
        heightInMeters = totalInches * 0.0254;
        weightInKg = pounds * 0.453592;
      } else {
        const { cm, kg } = inputs;

        if (!cm || cm <= 0) {
          throw new Error(t('errorInvalidHeight'));
        }
        if (!kg || kg <= 0) {
          throw new Error(t('errorInvalidWeight'));
        }

        heightInMeters = cm / 100;
        weightInKg = kg;
      }

      const bmi = weightInKg / (heightInMeters * heightInMeters);

      const minHealthyWeight = 18.5 * (heightInMeters * heightInMeters);
      const maxHealthyWeight = 24.9 * (heightInMeters * heightInMeters);

      let category: string;
      let categoryClass: string;
      let recommendation: string;

      if (bmi < 18.5) {
        category = t('categoryUnderweight');
        categoryClass = 'underweight';
        recommendation = t('recommendationUnderweight');
      } else if (bmi >= 18.5 && bmi < 25) {
        category = t('categoryNormal');
        categoryClass = 'normal';
        recommendation = t('recommendationNormal');
      } else if (bmi >= 25 && bmi < 30) {
        category = t('categoryOverweight');
        categoryClass = 'overweight';
        recommendation = t('recommendationOverweight');
      } else {
        category = t('categoryObese');
        categoryClass = 'obese';
        recommendation = t('recommendationObese');
      }

      let currentWeight: string;
      let healthyRange: string;

      if (currentUnit === 'imperial') {
        currentWeight = `${inputs.pounds.toFixed(1)} lbs`;
        healthyRange = `${(minHealthyWeight * 2.20462).toFixed(1)} - ${(maxHealthyWeight * 2.20462).toFixed(1)} lbs`;
      } else {
        currentWeight = `${inputs.kg.toFixed(1)} kg`;
        healthyRange = `${minHealthyWeight.toFixed(1)} - ${maxHealthyWeight.toFixed(1)} kg`;
      }

      setResults({
        bmi,
        category,
        categoryClass,
        currentWeight,
        healthyRange,
        recommendation,
      });

      setShowResults(true);

    } catch (error: any) {
      setErrorMessage(error.message);
      setShowResults(false);
    }
  };

  const containerStyle = getResponsiveStyle(
    { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
    { padding: '1rem' },
    { padding: '1.5rem' },
    { padding: '2rem' }
  );

  const headerStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '2rem' },
    {},
    { marginBottom: '2.5rem' },
    { marginBottom: '3rem' }
  );

  const h1Style = getResponsiveStyle(
    { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2 },
    { fontSize: '2rem' },
    { fontSize: '2.5rem' },
    { fontSize: '3rem' }
  );

  const summaryTextStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.6 },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const calculatorToolStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, rgba(240, 249, 255, 0.9) 0%, rgba(224, 242, 254, 0.9) 100%)',
      marginBottom: '2rem',
      borderRadius: '1rem',
    },
    { padding: '1.5rem 1rem' },
    { padding: '2rem 1.5rem', borderRadius: '1.25rem' },
    { padding: '3rem 2rem', borderRadius: '1.5rem' }
  );

  const toolContainerStyle = getResponsiveStyle(
    { maxWidth: '100%', margin: '0 auto' },
    {},
    { maxWidth: '900px' },
    { maxWidth: '1000px' }
  );

  const toolHeaderStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '1.5rem' },
    {},
    { marginBottom: '2rem' },
    { marginBottom: '2.5rem' }
  );

  const h2Style = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '1rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem' },
    { fontSize: '2rem' }
  );

  const calculatorLayoutStyle = getResponsiveStyle(
    { display: 'grid', gap: '1.5rem', marginTop: '2rem' },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: '1fr 1fr', gap: '2rem' }
  );

  const calculatorFormStyle = getResponsiveStyle(
    {
      background: 'white',
      borderRadius: '1rem',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    },
    { padding: '1.5rem' },
    { padding: '1.75rem' },
    { padding: '2rem' }
  );

  const unitTabsStyle = {
    display: 'flex',
    gap: '0.5rem',
    marginBottom: '1.5rem',
    background: '#f1f5f9',
    padding: '0.25rem',
    borderRadius: '0.5rem',
  };

  const unitTabStyle = (isActive: boolean) => ({
    flex: 1,
    padding: '0.625rem 1rem',
    border: 'none',
    background: isActive ? 'white' : 'transparent',
    color: isActive ? '#2563eb' : '#64748b',
    fontWeight: 500,
    cursor: 'pointer',
    borderRadius: '0.375rem',
    transition: 'all 0.2s',
    fontSize: '0.875rem',
    boxShadow: isActive ? '0 1px 3px rgba(0, 0, 0, 0.1)' : 'none',
  });

  const formGroupStyle = { marginBottom: '1.5rem' };

  const labelStyle = {
    display: 'block',
    marginBottom: '0.5rem',
    color: '#334155',
    fontWeight: 500,
    fontSize: '0.875rem',
  };

  const inputStyle = getResponsiveStyle(
    {
      width: '100%',
      border: '2px solid #e2e8f0',
      borderRadius: '0.5rem',
      transition: 'all 0.2s',
      boxSizing: 'border-box' as const,
    },
    { padding: '0.75rem', fontSize: '1rem' },
    { padding: '0.875rem', fontSize: '1rem' },
    { padding: '0.75rem', fontSize: '1rem' }
  );

  const heightInputsStyle = {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '0.75rem',
  };

  const inputWithUnitStyle = { position: 'relative' as const };

  const inputUnitStyle = {
    position: 'absolute' as const,
    right: '0.75rem',
    top: '50%',
    transform: 'translateY(-50%)',
    color: '#94a3b8',
    fontSize: '0.875rem',
    fontWeight: 500,
  };

  const genderOptionsStyle = { display: 'flex', gap: '1rem' };

  const genderOptionStyle = { flex: 1 };

  const genderLabelStyle = (isSelected: boolean) => ({
    display: 'block',
    padding: '0.75rem',
    border: `2px solid ${isSelected ? '#2563eb' : '#e2e8f0'}`,
    borderRadius: '0.5rem',
    textAlign: 'center' as const,
    cursor: 'pointer',
    transition: 'all 0.2s',
    fontWeight: 500,
    color: isSelected ? '#2563eb' : '#64748b',
    background: isSelected ? '#eff6ff' : 'transparent',
  });

  const calculateBtnStyle = getResponsiveStyle(
    {
      width: '100%',
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      color: 'white',
      border: 'none',
      borderRadius: '0.5rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.3s',
      boxShadow: '0 4px 6px -1px rgba(37, 99, 235, 0.3)',
    },
    { padding: '1rem', fontSize: '1rem' },
    { padding: '1rem', fontSize: '1rem' },
    { padding: '1rem', fontSize: '1rem' }
  );

  const resultsStyle = getResponsiveStyle(
    {
      background: 'white',
      borderRadius: '1rem',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    },
    { padding: '1.5rem' },
    { padding: '1.75rem' },
    { padding: '2rem' }
  );

  const bmiDisplayStyle = getResponsiveStyle(
    {
      textAlign: 'center' as const,
      background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
      borderRadius: '0.75rem',
      marginBottom: '1.5rem',
    },
    { padding: '1.5rem' },
    { padding: '1.75rem' },
    { padding: '2rem' }
  );

  const bmiValueStyle = getResponsiveStyle(
    { fontWeight: 700, color: '#2563eb', marginBottom: '0.5rem' },
    { fontSize: '2.5rem' },
    { fontSize: '2.75rem' },
    { fontSize: '3rem' }
  );

  const bmiCategoryStyle = (categoryClass: string) => {
    const colors: { [key: string]: string } = {
      underweight: '#0ea5e9',
      normal: '#10b981',
      overweight: '#f59e0b',
      obese: '#ef4444',
    };

    return getResponsiveStyle(
      { fontWeight: 600, marginBottom: '0.5rem', color: colors[categoryClass] || '#64748b' },
      { fontSize: '1.125rem' },
      { fontSize: '1.25rem' },
      { fontSize: '1.25rem' }
    );
  };

  const resultItemStyle = {
    padding: '1rem',
    background: '#f8fafc',
    borderRadius: '0.5rem',
    marginBottom: '1rem',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  };

  const resultLabelStyle = {
    color: '#64748b',
    fontWeight: 500,
    fontSize: '0.875rem',
  };

  const resultValueStyle = {
    color: '#1e293b',
    fontWeight: 600,
    fontSize: '1rem',
  };

  const bmiChartStyle = getResponsiveStyle(
    {
      background: '#f8fafc',
      borderRadius: '0.75rem',
    },
    { marginTop: '1.5rem', padding: '1.25rem' },
    { marginTop: '1.5rem', padding: '1.5rem' },
    { marginTop: '1.5rem', padding: '1.5rem' }
  );

  const bmiRangeStyle = (isActive: boolean) => ({
    display: 'flex',
    alignItems: 'center',
    padding: '0.75rem',
    marginBottom: '0.5rem',
    borderRadius: '0.375rem',
    transition: 'all 0.2s',
    background: isActive ? 'white' : 'transparent',
    boxShadow: isActive ? '0 2px 4px rgba(0, 0, 0, 0.1)' : 'none',
  });

  const rangeColorStyle = (category: string) => {
    const colors: { [key: string]: string } = {
      underweight: '#0ea5e9',
      normal: '#10b981',
      overweight: '#f59e0b',
      obese: '#ef4444',
    };

    return {
      width: '1rem',
      height: '1rem',
      borderRadius: '0.25rem',
      marginRight: '0.75rem',
      background: colors[category],
    };
  };

  const weightInfoStyle = (categoryClass: string) => {
    const isNormal = categoryClass === 'normal';

    return getResponsiveStyle(
      {
        borderRadius: '0.75rem',
        borderLeft: `4px solid ${isNormal ? '#10b981' : '#f59e0b'}`,
        background: isNormal
          ? 'linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%)'
          : 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
      },
      { marginTop: '1.5rem', padding: '1rem' },
      { marginTop: '1.5rem', padding: '1.25rem' },
      { marginTop: '1.5rem', padding: '1.25rem' }
    );
  };

  const errorMessageStyle = {
    background: '#fee2e2',
    color: '#991b1b',
    padding: '1rem',
    borderRadius: '0.5rem',
    marginBottom: '1rem',
    fontSize: '0.875rem',
    display: errorMessage ? 'block' : 'none',
  };

  const glassCardStyle = getResponsiveStyle(
    {
      background: colors.glassBg,
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      border: `1px solid ${colors.glassBorder}`,
      borderRadius: '1rem',
      boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
      marginBottom: '2rem',
    },
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2.5rem' }
  );

  const paragraphStyle = getResponsiveStyle(
    { lineHeight: 1.6, color: colors.textSecondary, marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.125rem' }
  );

  const listStyle = {
    marginLeft: '1.5rem',
    marginBottom: '1rem',
  };

  const listItemStyle = getResponsiveStyle(
    { marginBottom: '0.75rem', color: colors.textSecondary, lineHeight: 1.6 },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.125rem' }
  );

  const h3Style = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginTop: '1.5rem', marginBottom: '1rem' },
    { fontSize: '1.25rem' },
    { fontSize: '1.375rem' },
    { fontSize: '1.5rem' }
  );

  const faqItemStyle = {
    marginBottom: '1.5rem',
  };

  const referenceStyle = {
    fontSize: '0.875rem',
    color: colors.textSecondary,
    marginBottom: '0.5rem',
    lineHeight: 1.5,
  };

  return (
    <div style={containerStyle}>
      <header style={headerStyle}>
        <h1 style={h1Style}>{t('title')}</h1>
        <p style={summaryTextStyle}>{t('subtitle')}</p>
      </header>

      <section style={calculatorToolStyle}>
        <div style={toolContainerStyle}>
          <div style={toolHeaderStyle}>
            <h2 style={h2Style}>{t('toolTitle')}</h2>
            <p style={summaryTextStyle}>{t('toolSubtitle')}</p>
          </div>

          <div style={calculatorLayoutStyle}>
            <div style={calculatorFormStyle}>
              <div style={unitTabsStyle}>
                <button
                  type="button"
                  style={unitTabStyle(currentUnit === 'imperial')}
                  onClick={() => setCurrentUnit('imperial')}
                >
                  {t('unitImperial')}
                </button>
                <button
                  type="button"
                  style={unitTabStyle(currentUnit === 'metric')}
                  onClick={() => setCurrentUnit('metric')}
                >
                  {t('unitMetric')}
                </button>
              </div>

              <div style={errorMessageStyle}>
                {errorMessage}
              </div>

              <form onSubmit={handleCalculate}>
                <div style={formGroupStyle}>
                  <label style={labelStyle} htmlFor="age">{t('labelAge')}</label>
                  <input
                    style={inputStyle}
                    type="number"
                    id="age"
                    min="2"
                    max="120"
                    value={inputs.age}
                    onChange={(e) => setInputs({...inputs, age: parseInt(e.target.value)})}
                    required
                  />
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle}>{t('labelGender')}</label>
                  <div style={genderOptionsStyle}>
                    <div style={genderOptionStyle}>
                      <input
                        type="radio"
                        id="male"
                        name="gender"
                        value="male"
                        checked={inputs.gender === 'male'}
                        onChange={(e) => setInputs({...inputs, gender: e.target.value as 'male' | 'female'})}
                        style={{ display: 'none' }}
                      />
                      <label
                        htmlFor="male"
                        style={genderLabelStyle(inputs.gender === 'male')}
                      >
                        {t('genderMale')}
                      </label>
                    </div>
                    <div style={genderOptionStyle}>
                      <input
                        type="radio"
                        id="female"
                        name="gender"
                        value="female"
                        checked={inputs.gender === 'female'}
                        onChange={(e) => setInputs({...inputs, gender: e.target.value as 'male' | 'female'})}
                        style={{ display: 'none' }}
                      />
                      <label
                        htmlFor="female"
                        style={genderLabelStyle(inputs.gender === 'female')}
                      >
                        {t('genderFemale')}
                      </label>
                    </div>
                  </div>
                </div>

                {currentUnit === 'imperial' ? (
                  <div>
                    <div style={formGroupStyle}>
                      <label style={labelStyle}>{t('labelHeight')}</label>
                      <div style={heightInputsStyle}>
                        <div style={inputWithUnitStyle}>
                          <input
                            style={inputStyle}
                            type="number"
                            id="feet"
                            min="0"
                            max="8"
                            value={inputs.feet}
                            onChange={(e) => setInputs({...inputs, feet: parseInt(e.target.value)})}
                            required
                          />
                          <span style={inputUnitStyle}>{t('unitFeet')}</span>
                        </div>
                        <div style={inputWithUnitStyle}>
                          <input
                            style={inputStyle}
                            type="number"
                            id="inches"
                            min="0"
                            max="11"
                            value={inputs.inches}
                            onChange={(e) => setInputs({...inputs, inches: parseInt(e.target.value)})}
                            required
                          />
                          <span style={inputUnitStyle}>{t('unitInches')}</span>
                        </div>
                      </div>
                    </div>

                    <div style={formGroupStyle}>
                      <label style={labelStyle} htmlFor="pounds">{t('labelWeight')}</label>
                      <div style={inputWithUnitStyle}>
                        <input
                          style={inputStyle}
                          type="number"
                          id="pounds"
                          min="1"
                          max="1000"
                          value={inputs.pounds}
                          onChange={(e) => setInputs({...inputs, pounds: parseFloat(e.target.value)})}
                          step="0.1"
                          required
                        />
                        <span style={inputUnitStyle}>{t('unitPounds')}</span>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div>
                    <div style={formGroupStyle}>
                      <label style={labelStyle} htmlFor="cm">{t('labelHeight')}</label>
                      <div style={inputWithUnitStyle}>
                        <input
                          style={inputStyle}
                          type="number"
                          id="cm"
                          min="1"
                          max="300"
                          value={inputs.cm}
                          onChange={(e) => setInputs({...inputs, cm: parseFloat(e.target.value)})}
                          step="0.1"
                          required
                        />
                        <span style={inputUnitStyle}>{t('unitCm')}</span>
                      </div>
                    </div>

                    <div style={formGroupStyle}>
                      <label style={labelStyle} htmlFor="kg">{t('labelWeight')}</label>
                      <div style={inputWithUnitStyle}>
                        <input
                          style={inputStyle}
                          type="number"
                          id="kg"
                          min="1"
                          max="500"
                          value={inputs.kg}
                          onChange={(e) => setInputs({...inputs, kg: parseFloat(e.target.value)})}
                          step="0.1"
                          required
                        />
                        <span style={inputUnitStyle}>{t('unitKg')}</span>
                      </div>
                    </div>
                  </div>
                )}

                <button type="submit" style={calculateBtnStyle}>
                  {t('calculateButton')}
                </button>
              </form>
            </div>

            {showResults && (
              <div style={resultsStyle}>
                <h3 style={h2Style}>{t('resultsTitle')}</h3>

                <div style={bmiDisplayStyle}>
                  <div style={bmiValueStyle}>{results.bmi.toFixed(1)}</div>
                  <div style={bmiCategoryStyle(results.categoryClass)}>{results.category}</div>
                  <div style={{ color: '#64748b', fontSize: '0.875rem' }}>{t('bmiLabel')}</div>
                </div>

                <div style={resultItemStyle}>
                  <span style={resultLabelStyle}>{t('resultYourWeight')}</span>
                  <span style={resultValueStyle}>{results.currentWeight}</span>
                </div>

                <div style={resultItemStyle}>
                  <span style={resultLabelStyle}>{t('resultHealthyRange')}</span>
                  <span style={resultValueStyle}>{results.healthyRange}</span>
                </div>

                <div style={resultItemStyle}>
                  <span style={resultLabelStyle}>{t('resultWeightStatus')}</span>
                  <span style={resultValueStyle}>{results.category}</span>
                </div>

                <div style={bmiChartStyle}>
                  <h4 style={{ fontSize: '0.875rem', color: '#64748b', marginBottom: '1rem', fontWeight: 600, textTransform: 'uppercase' as const, letterSpacing: '0.05em' }}>
                    {t('bmiCategoriesTitle')}
                  </h4>
                  <div style={bmiRangeStyle(results.categoryClass === 'underweight')}>
                    <div style={rangeColorStyle('underweight')}></div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, color: '#1e293b', fontSize: '0.875rem' }}>
                        {t('categoryUnderweight')}
                      </div>
                      <div style={{ color: '#64748b', fontSize: '0.75rem' }}>
                        {t('rangeUnderweight')}
                      </div>
                    </div>
                  </div>
                  <div style={bmiRangeStyle(results.categoryClass === 'normal')}>
                    <div style={rangeColorStyle('normal')}></div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, color: '#1e293b', fontSize: '0.875rem' }}>
                        {t('categoryNormal')}
                      </div>
                      <div style={{ color: '#64748b', fontSize: '0.75rem' }}>
                        {t('rangeNormal')}
                      </div>
                    </div>
                  </div>
                  <div style={bmiRangeStyle(results.categoryClass === 'overweight')}>
                    <div style={rangeColorStyle('overweight')}></div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, color: '#1e293b', fontSize: '0.875rem' }}>
                        {t('categoryOverweight')}
                      </div>
                      <div style={{ color: '#64748b', fontSize: '0.75rem' }}>
                        {t('rangeOverweight')}
                      </div>
                    </div>
                  </div>
                  <div style={bmiRangeStyle(results.categoryClass === 'obese')}>
                    <div style={rangeColorStyle('obese')}></div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, color: '#1e293b', fontSize: '0.875rem' }}>
                        {t('categoryObese')}
                      </div>
                      <div style={{ color: '#64748b', fontSize: '0.75rem' }}>
                        {t('rangeObese')}
                      </div>
                    </div>
                  </div>
                </div>

                <div style={weightInfoStyle(results.categoryClass)}>
                  <h4 style={{ color: '#1e293b', fontSize: '1rem', marginBottom: '0.5rem', fontWeight: 600 }}>
                    {t('recommendationTitle')}
                  </h4>
                  <p style={{ color: '#334155', fontSize: '0.875rem', lineHeight: 1.5, margin: 0 }}>
                    {results.recommendation}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('introTitle')}</h2>
        <p style={paragraphStyle}>{t('introParagraph1')}</p>
        <p style={paragraphStyle}>{t('introParagraph2')}</p>
        <p style={paragraphStyle}>{t('introParagraph3')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('howToUseTitle')}</h2>
        <ol style={listStyle}>
          <li style={listItemStyle}>{t('howToUseStep1')}</li>
          <li style={listItemStyle}>{t('howToUseStep2')}</li>
          <li style={listItemStyle}>{t('howToUseStep3')}</li>
          <li style={listItemStyle}>{t('howToUseStep4')}</li>
          <li style={listItemStyle}>{t('howToUseStep5')}</li>
        </ol>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('insightsTitle')}</h2>

        <h3 style={h3Style}>{t('insightsBmiFormulaTitle')}</h3>
        <p style={paragraphStyle}>{t('insightsBmiFormula1')}</p>
        <p style={paragraphStyle}>{t('insightsBmiFormula2')}</p>

        <h3 style={h3Style}>{t('insightsCategoriesTitle')}</h3>
        <ul style={listStyle}>
          <li style={listItemStyle}>{t('insightsCategory1')}</li>
          <li style={listItemStyle}>{t('insightsCategory2')}</li>
          <li style={listItemStyle}>{t('insightsCategory3')}</li>
          <li style={listItemStyle}>{t('insightsCategory4')}</li>
        </ul>

        <h3 style={h3Style}>{t('insightsLimitationsTitle')}</h3>
        <p style={paragraphStyle}>{t('insightsLimitations1')}</p>
        <p style={paragraphStyle}>{t('insightsLimitations2')}</p>

        <h3 style={h3Style}>{t('insightsBestPracticesTitle')}</h3>
        <ul style={listStyle}>
          <li style={listItemStyle}>{t('insightsBestPractice1')}</li>
          <li style={listItemStyle}>{t('insightsBestPractice2')}</li>
          <li style={listItemStyle}>{t('insightsBestPractice3')}</li>
          <li style={listItemStyle}>{t('insightsBestPractice4')}</li>
        </ul>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('detailsTitle')}</h2>

        <h3 style={h3Style}>{t('detailsHealthRisksTitle')}</h3>
        <p style={paragraphStyle}>{t('detailsHealthRisks')}</p>

        <h3 style={h3Style}>{t('detailsEthnicityTitle')}</h3>
        <p style={paragraphStyle}>{t('detailsEthnicity')}</p>

        <h3 style={h3Style}>{t('detailsWaistTitle')}</h3>
        <p style={paragraphStyle}>{t('detailsWaist')}</p>

        <h3 style={h3Style}>{t('detailsNextStepsTitle')}</h3>
        <p style={paragraphStyle}>{t('detailsNextSteps')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('faqTitle')}</h2>

        <div style={faqItemStyle}>
          <h3 style={h3Style}>{t('faqQuestion1')}</h3>
          <p style={paragraphStyle}>{t('faqAnswer1')}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={h3Style}>{t('faqQuestion2')}</h3>
          <p style={paragraphStyle}>{t('faqAnswer2')}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={h3Style}>{t('faqQuestion3')}</h3>
          <p style={paragraphStyle}>{t('faqAnswer3')}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={h3Style}>{t('faqQuestion4')}</h3>
          <p style={paragraphStyle}>{t('faqAnswer4')}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={h3Style}>{t('faqQuestion5')}</h3>
          <p style={paragraphStyle}>{t('faqAnswer5')}</p>
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('referencesTitle')}</h2>
        <p style={referenceStyle}>{t('reference1')}</p>
        <p style={referenceStyle}>{t('reference2')}</p>
        <p style={referenceStyle}>{t('reference3')}</p>
        <p style={referenceStyle}>{t('reference4')}</p>
        <p style={referenceStyle}>{t('reference5')}</p>
      </section>
    </div>
  );
}